import { storageThrottle } from './storageThrottle';

interface ImageCacheEntry {
  url: string;
  timestamp: number;
  blob?: string;
}

const IMAGE_CACHE_VERSION = 1;
const IMAGE_CACHE_PREFIX = 'footy_img_';
const IMAGE_CACHE_TTL = 86400000;
const PRELOAD_COOLDOWN_MS = 60000; // 1 minute cooldown between preloads of the same image

class ImageCache {
  private cache: Map<string, ImageCacheEntry>;
  private loadingImages: Map<string, Promise<string>>;
  private lastPreloadTime: Map<string, number>; // Track last preload time per URL
  private sessionPreloaded: Set<string>; // Track what's been preloaded this session

  constructor() {
    this.cache = new Map();
    this.loadingImages = new Map();
    this.lastPreloadTime = new Map();
    this.sessionPreloaded = new Set();
    this.loadFromStorage();
  }

  private getCacheKey(url: string): string {
    // Use full URL hash to avoid collisions
    return IMAGE_CACHE_PREFIX + btoa(url);
  }

  get(url: string): string | null {
    const entry = this.cache.get(url);
    if (!entry) {
      const stored = this.getFromStorage(url);
      if (stored) {
        this.cache.set(url, stored);
        return stored.blob || stored.url;
      }
      return null;
    }

    const now = Date.now();
    if (now - entry.timestamp > IMAGE_CACHE_TTL) {
      this.cache.delete(url);
      this.removeFromStorage(url);
      return null;
    }

    return entry.blob || entry.url;
  }

  set(url: string, blob?: string): void {
    const entry: ImageCacheEntry = {
      url,
      timestamp: Date.now(),
      blob
    };

    this.cache.set(url, entry);
    this.saveToStorage(url, entry);
  }

  preload(url: string): Promise<string> {
    const shortUrl = url.substring(url.lastIndexOf('/') + 1);

    // Check if already cached
    const cached = this.get(url);
    if (cached) {
      storageThrottle.markAccessed(url);
      console.log(`[ImageCache] ✓ Cached: ${shortUrl}`);
      return Promise.resolve(cached);
    }

    // Check if preloaded this session already
    if (this.sessionPreloaded.has(url)) {
      console.log(`[ImageCache] ✓ Session preloaded: ${shortUrl}`);
      return Promise.resolve(url);
    }

    // Check storage throttle BEFORE any other checks
    if (!storageThrottle.shouldAllowRequest(url)) {
      console.log(`[ImageCache] ⛔ Throttled by storage limiter: ${shortUrl}`);
      return Promise.resolve(url);
    }

    // Check cooldown period to prevent excessive preloading
    const lastPreload = this.lastPreloadTime.get(url);
    if (lastPreload) {
      const timeSinceLastPreload = Date.now() - lastPreload;
      if (timeSinceLastPreload < PRELOAD_COOLDOWN_MS) {
        console.log(`[ImageCache] 🕐 Cooldown (${Math.round((PRELOAD_COOLDOWN_MS - timeSinceLastPreload) / 1000)}s remaining): ${shortUrl}`);
        return Promise.resolve(url);
      }
    }

    // Check for in-flight requests
    const inFlight = this.loadingImages.get(url);
    if (inFlight) {
      console.log(`[ImageCache] ⏳ In-flight: ${shortUrl}`);
      return inFlight;
    }

    console.log(`[ImageCache] 🆕 New preload: ${shortUrl}`);

    // Record preload time BEFORE creating the promise
    this.lastPreloadTime.set(url, Date.now());
    this.sessionPreloaded.add(url);

    // Create promise AND store it in loadingImages BEFORE creating Image object
    let resolvePromise: (value: string) => void;
    const promise = new Promise<string>((resolve) => {
      resolvePromise = resolve;
    });

    // Store promise IMMEDIATELY before any Image creation
    this.loadingImages.set(url, promise);

    // Now create the Image object that will trigger the fetch
    const img = new Image();
    img.onload = () => {
      this.set(url);
      this.loadingImages.delete(url);
      console.log(`[ImageCache] ✅ Loaded: ${shortUrl}`);
      resolvePromise!(url);
    };
    img.onerror = () => {
      this.loadingImages.delete(url);
      console.log(`[ImageCache] ❌ Error: ${shortUrl}`);
      resolvePromise!(url);
    };

    // Setting img.src triggers the network fetch
    img.src = url;
    console.log(`[ImageCache] 📡 Fetching: ${shortUrl}`);

    return promise;
  }

  private saveToStorage(url: string, entry: ImageCacheEntry): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const cacheKey = this.getCacheKey(url);
      const dataToStore = {
        url: entry.url,
        timestamp: entry.timestamp,
        version: IMAGE_CACHE_VERSION
      };
      localStorage.setItem(cacheKey, JSON.stringify(dataToStore));
    } catch (error) {
      console.warn('Failed to save image cache to localStorage:', error);
    }
  }

  private getFromStorage(url: string): ImageCacheEntry | null {
    if (typeof localStorage === 'undefined') return null;

    try {
      const cacheKey = this.getCacheKey(url);
      const stored = localStorage.getItem(cacheKey);
      if (!stored) return null;

      const entry = JSON.parse(stored);

      if (entry.version !== IMAGE_CACHE_VERSION) {
        this.removeFromStorage(url);
        return null;
      }

      const now = Date.now();
      if (now - entry.timestamp > IMAGE_CACHE_TTL) {
        this.removeFromStorage(url);
        return null;
      }

      return {
        url: entry.url,
        timestamp: entry.timestamp
      };
    } catch (error) {
      console.warn('Failed to load image cache from localStorage:', error);
      return null;
    }
  }

  private removeFromStorage(url: string): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const cacheKey = this.getCacheKey(url);
      localStorage.removeItem(cacheKey);
    } catch (error) {
      // Fail silently
    }
  }

  private loadFromStorage(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(IMAGE_CACHE_PREFIX)) {
          try {
            const stored = localStorage.getItem(key);
            if (!stored) return;

            const entry = JSON.parse(stored);
            if (entry.version === IMAGE_CACHE_VERSION) {
              const now = Date.now();
              if (now - entry.timestamp <= IMAGE_CACHE_TTL) {
                this.cache.set(entry.url, {
                  url: entry.url,
                  timestamp: entry.timestamp
                });
              } else {
                localStorage.removeItem(key);
              }
            } else {
              localStorage.removeItem(key);
            }
          } catch {
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.warn('Failed to load image cache from localStorage:', error);
    }
  }

  clear(): void {
    this.cache.clear();
    this.loadingImages.clear();
    this.lastPreloadTime.clear();
    this.sessionPreloaded.clear();

    if (typeof localStorage === 'undefined') return;

    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(IMAGE_CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear image cache:', error);
    }
  }

  preloadMultiple(urls: string[]): Promise<string[]> {
    return Promise.all(urls.map(url => this.preload(url)));
  }
}

export const imageCache = new ImageCache();
